import '@node-real/walletkit/styles.css';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import NextApp, { AppProps } from 'next/app';
import { getMenus } from '@bnb-chain/space';
import { NextPageContext } from 'next';
import { pick } from 'accept-language-parser';
import {
  CanonicalBridgeProvider,
  ICustomizedBridgeConfig,
} from '@bnb-chain/canonical-bridge-widget';
import { useMemo } from 'react';
import { Provider as StoreProvider } from 'react-redux';

import { Layout } from '@/core/components/Layout';
import { wrapper } from '@/core/store/store';
import { DEFAULT_LOCALE, en, SUPPORTED_LOCALES } from '@/modules/i18n/locales';
import { updateI18n } from '@/modules/i18n/action';
import { ThemeProvider } from '@/core/theme/ThemeProvider';
import { env } from '@/core/configs/env';
import { setFooterMedia, setFooterMenus } from '@/modules/common/action';
import { setupCmsData } from '@/modules/common/cms';
import { ExternalBridgesPanel } from '@/core/components/ExternalBridgesPanel';
import { dark } from '@/core/theme/dark';
import { useFetchTransferConfig } from '@/modules/transfer/api/useFetchTransferConfig';
import { IntlProvider } from '@/modules/i18n/IntlProvider';
import { useAppSelector } from '@/core/store/hooks';
import { SEO } from '@/core/components/SEO';
import { AppWalletProvider } from '@/modules/wallet/AppWalletProvider';
import { useWalletModal } from '@/modules/wallet/hooks/useWalletModal';
import { chains } from '@/core/configs/chains';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnReconnect: true,
      refetchOnWindowFocus: false,
      retry: 2,
    },
  },
});

export default function App(props: AppProps) {
  const { Component, pageProps } = props;
  const { store } = wrapper.useWrappedStore(props);

  if (pageProps?.statusCode === 404) {
    return <Component {...pageProps} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <StoreProvider store={store}>
          <IntlProvider>
            <AppWalletProvider chainConfigs={chains}>
              <BridgeWidget>
                <SEO />
                <Layout>
                  <Component {...pageProps} />
                </Layout>
              </BridgeWidget>
            </AppWalletProvider>
          </IntlProvider>
        </StoreProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

function BridgeWidget({ children }: React.PropsWithChildren) {
  const language = useAppSelector((state) => state.i18n.locale);
  const messages = useAppSelector((state) => state.i18n.messages);

  const { data: transferConfig } = useFetchTransferConfig();
  const { onOpen } = useWalletModal();

  const config: ICustomizedBridgeConfig = useMemo(
    () => ({
      locale: {
        language,
        messages,
      },
      theme: {
        colorMode: 'dark',
        colors: {
          light: {},
          dark,
        },
      },
      http: {
        // refetchingInterval: 30 * 1000, // 30s
        refetchingInterval: 3 * 60 * 60 * 1000, // 30s
        apiTimeOut: 60 * 1000, // 30s
        serverEndpoint: env.WIDGET_SERVER_ENDPOINT,
        deBridgeAccessToken: env.DEBRIDGE_ACCESS_TOKEN,
        deBridgeReferralCode: env.DEBRIDGE_REFERRAL_CODE,
        feeReloadMaxTime: Number(env.FEE_RELOAD_MAX_TIME),
      },
      transfer: {
        chainConfigs: chains,
        ...transferConfig,
      },
      components: {
        routeContentBottom: <ExternalBridgesPanel />,
      },
      onClickConnectWalletButton: onOpen,
    }),
    [language, messages, onOpen, transferConfig],
  );

  return <CanonicalBridgeProvider config={config}>{children}</CanonicalBridgeProvider>;
}

App.getInitialProps = wrapper.getInitialAppProps((store) => async (appContext) => {
  try {
    const locale = getLocale(appContext.ctx);

    if (appContext.ctx.res?.statusCode === 404) {
      return {
        pageProps: {
          ...(await NextApp.getInitialProps(appContext)).pageProps,
          statusCode: 404,
        },
      };
    }

    const messages = { ...en };

    await store.dispatch(
      updateI18n({
        locale,
        messages,
      }),
    );

    const resArr = await Promise.allSettled([
      await getMenus(env.DIRECTUS_API_URL, locale, env.DIRECTUS_CDN_URL),
      await store.dispatch(setupCmsData()),
    ]);

    if (resArr[0].status === 'fulfilled') {
      const { footerMenus, footerMedia } = resArr[0].value;
      await store.dispatch(setFooterMenus(footerMenus));
      await store.dispatch(setFooterMedia(footerMedia));
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
  }

  const appProps = await NextApp.getInitialProps(appContext);
  return { ...appProps };
});

function getLocale(ctx: NextPageContext) {
  const queryLocale = pick(SUPPORTED_LOCALES, ctx.asPath ?? '');
  const headerLocale = pick(SUPPORTED_LOCALES, ctx.req?.headers['accept-language'] ?? '');

  const locale = queryLocale || headerLocale || DEFAULT_LOCALE;
  const targetUrl = `/${locale}${env.BASE_PATH}`;

  if (!ctx.asPath?.startsWith(targetUrl)) {
    ctx.res?.writeHead(302, { Location: targetUrl }).end();
  }

  return locale;
}
